/**
 * Keep an in-progress form in the browser so a crash, a reload or a closed
 * tab does not throw away what someone typed. Per-viewer, best effort:
 * every call is wrapped because storage can be full, blocked or private.
 */
export interface StoredDraft<T> {
  value: T;
  savedAt: number; // epoch ms
}

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function saveDraft<T>(key: string, value: T): void {
  try {
    const entry: StoredDraft<T> = { value, savedAt: Date.now() };
    window.localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // storage unavailable; nothing to do
  }
}

export function loadDraft<T>(key: string): StoredDraft<T> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft<T>;
    if (!parsed || typeof parsed.savedAt !== 'number' || !('value' in parsed)) return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      clearDraft(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** True when the draft holds something other than what the server has. */
export function draftDiffers<T extends object>(draft: T, current: T): boolean {
  return JSON.stringify(draft) !== JSON.stringify(current);
}
